/**
 * Onboarding progress cache (localStorage)
 *
 * DynamoDB (`onboarding_steps` / `onboarding_started` on the tenant metadata
 * item) is the source of truth — or, in local dev with the onboarding override
 * enabled, the Postgres table standing in for it; these keys are a same-session
 * synchronous cache so the router guard and the checklist view can read progress
 * without waiting on a network round-trip.
 *
 * Kept in its own dependency-free module — rather than inside settings.store.ts —
 * because auth.store.ts needs `clearOnboardingProgress()` synchronously during
 * `clearAuth()`, and settings.store.ts imports auth.store.ts (so a static import
 * the other way would be circular).  settings.store.ts re-exports everything
 * here, so existing import paths keep working.
 *
 * Invariant these helpers exist to serve: `is_settings_configured` is the single
 * authority on whether a tenant has finished onboarding.  These values are
 * progress hints that are only ever read while it is false, and are cleared when
 * it is true — they must never be used to derive or override it.
 */
import { WIZARD_STEP_KEYS } from './wizardSteps';

const WIZARD_PROGRESS_KEY = 'settingsWizardProgress';
const ONBOARDING_STARTED_KEY = 'settingsOnboardingStarted';
const IS_SETTINGS_CONFIGURED_KEY = 'isSettingsConfigured';

/**
 * Read persisted per-step completion from localStorage.
 * Returns the list of step keys marked complete — order-independent, so
 * steps can be completed in any order (no "gaps" concept anymore).
 */
export function readCompletedSteps(): string[] {
  try {
    const raw = localStorage.getItem(WIZARD_PROGRESS_KEY);
    if (!raw) return [];
    const progress = JSON.parse(raw) as Record<string, boolean>;
    return WIZARD_STEP_KEYS.filter((k) => progress[k] === true);
  } catch {
    return [];
  }
}

/** Write per-step completion to localStorage. */
export function writeCompletedSteps(steps: string[]): void {
  const progress: Record<string, boolean> = {};
  for (const k of WIZARD_STEP_KEYS) progress[k] = steps.includes(k);
  localStorage.setItem(WIZARD_PROGRESS_KEY, JSON.stringify(progress));
}

/**
 * Read the "onboarding started" flag from localStorage — separate from
 * `completedSteps` because an admin can click past the welcome splash
 * without completing (or even opening) a single step, and that choice
 * must still be remembered so the checklist keeps showing instead of the
 * splash on the next visit.
 */
export function readOnboardingStarted(): boolean {
  return localStorage.getItem(ONBOARDING_STARTED_KEY) === 'true';
}

export function writeOnboardingStarted(started: boolean): void {
  localStorage.setItem(ONBOARDING_STARTED_KEY, String(started));
}

/**
 * Drop both cached progress keys.
 *
 * Called when onboarding completes (the values are meaningless once
 * `is_settings_configured` is true, and clearing them stops a stale read from
 * ever contradicting it) and on logout — where leaving them behind would let one
 * admin's partial progress be read as the next admin's on a shared workstation.
 */
export function clearOnboardingProgress(): void {
  localStorage.removeItem(WIZARD_PROGRESS_KEY);
  localStorage.removeItem(ONBOARDING_STARTED_KEY);
}

/**
 * Cached `is_settings_configured`, or null if never persisted this session.
 * The single place that key is read from localStorage — see
 * `resolveIsSettingsConfigured()` below for the full precedence chain.
 */
export function readIsSettingsConfigured(): boolean | null {
  const stored = localStorage.getItem(IS_SETTINGS_CONFIGURED_KEY);
  return stored === null ? null : stored === 'true';
}

/** The single place `is_settings_configured` is written to localStorage. */
export function writeIsSettingsConfigured(value: boolean): void {
  localStorage.setItem(IS_SETTINGS_CONFIGURED_KEY, String(value));
}

/** The single place `is_settings_configured` is cleared from localStorage. */
export function clearIsSettingsConfigured(): void {
  localStorage.removeItem(IS_SETTINGS_CONFIGURED_KEY);
}

// ── BEGIN LOCAL_ONBOARDING_OVERRIDE ─────────────────────────────────────────
/**
 * Resolve `is_settings_configured` across every source, in priority order:
 * local override (if active, asks the API) -> localStorage -> JWT claim ->
 * unresolved (null).
 *
 * The one place `settingsOnboardingGuard` and `checkSettingsOnboarding()` both
 * call, so their fallback chains can never drift apart again. Returns null
 * rather than picking a default so each caller keeps deciding its own
 * unresolved-case behaviour (the guard allows navigation through; the store
 * defaults to configured).
 *
 * `@/core/config` and the settings API are dynamically imported here, not
 * statically, so this file's many other consumers don't newly need to mock
 * `@/core/config` just because this one function exists.
 */
export async function resolveIsSettingsConfigured(jwtClaim: boolean | null): Promise<boolean | null> {
  const { isLocalOnboardingOverrideActive } = await import('@/core/config');
  if (isLocalOnboardingOverrideActive()) {
    try {
      const { checkSettingsConfigured } = await import('@/modules/settings/services/settings.api');
      return await checkSettingsConfigured();
    } catch {
      // Fall through to the normal precedence chain — a dead local API must
      // never lock anyone out of the app.
    }
  }
  const stored = readIsSettingsConfigured();
  if (stored !== null) return stored;
  return jwtClaim;
}
// ── END LOCAL_ONBOARDING_OVERRIDE ───────────────────────────────────────────
